import { AnimatePresence, motion } from "framer-motion";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type LucideIcon } from "lucide-react";

import { icons } from "@/objects/@icons";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/components/ui/utils";
import { Typography } from "@/components/ui/typography";
import type {
  AgentScenario,
  FlowNodeDef,
  FlowStepDef,
  SubCallDef,
} from "./agent-scenario";

// ── Phases ───────────────────────────────────────────────────────────────────

type Phase =
  | "streaming-pre"
  | "card-thinking"
  | "card-done"
  | "streaming-post"
  | "pause";

type CardPhase = "activating" | "thinking" | "done" | "collapsed";

// ── Streaming Text Hook ─────────────────────────────────────────────────────

function useStreamingText(text: string, isActive: boolean, durationMs: number) {
  const [displayed, setDisplayed] = useState("");
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isActive || !text) {
      setDisplayed("");
      return;
    }
    const totalChars = text.length;
    if (totalChars === 0) return;
    const charsPerMs = totalChars / (durationMs * 0.85);
    let startTime: number | null = null;
    let currentLength = 0;

    const animate = (ts: number) => {
      if (startTime === null) startTime = ts;
      const elapsed = ts - startTime;
      const target = Math.min(Math.floor(elapsed * charsPerMs), totalChars);
      if (target !== currentLength) {
        currentLength = target;
        setDisplayed(text.slice(0, target));
      }
      if (currentLength < totalChars) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [text, isActive, durationMs]);

  return displayed;
}

function StreamingMono({
  text,
  isStreaming,
  durationMs,
  onUpdate,
}: {
  text: string;
  isStreaming: boolean;
  durationMs: number;
  onUpdate?: () => void;
}) {
  const safe = text ?? "";
  const displayed = useStreamingText(safe, isStreaming, durationMs);

  useEffect(() => {
    if (isStreaming && onUpdate) onUpdate();
  }, [displayed, isStreaming, onUpdate]);

  return <>{isStreaming ? displayed : safe}</>;
}

// ── Stats display ────────────────────────────────────────────────────────────

function StatsDisplay({
  stats,
}: {
  stats?: { added?: number; updated?: number; removed?: number };
}) {
  if (!stats) return null;
  const { added = 0, updated = 0, removed = 0 } = stats;
  if (!added && !updated && !removed) return null;

  return (
    <div className="flex items-center gap-1.5">
      {added > 0 && (
        <Typography
          level="body-xs"
          className="font-mono [font-weight:700] text-[11px] text-foreground"
        >
          +{added}
        </Typography>
      )}
      {updated > 0 && (
        <Typography
          level="body-xs"
          className="font-mono [font-weight:700] text-[11px] text-foreground"
        >
          ~{updated}
        </Typography>
      )}
      {removed > 0 && (
        <Typography
          level="body-xs"
          className="font-mono [font-weight:700] text-[11px] text-destructive"
        >
          -{removed}
        </Typography>
      )}
    </div>
  );
}

// ── Recursive SubCall Card ───────────────────────────────────────────────────

function SubCallCard({
  subCall,
  cardPhase,
  durationMs,
  depth,
  iterationLabel,
  onScrollParent,
}: {
  subCall: SubCallDef;
  cardPhase: CardPhase;
  durationMs: number;
  depth: number;
  iterationLabel?: string;
  onScrollParent: () => void;
}) {
  const isThinking = cardPhase === "thinking";
  const isDone = cardPhase === "done" || cardPhase === "collapsed";
  const isClickable = isDone;
  const [manualToggle, setManualToggle] = useState<boolean | null>(null);

  // Reset manual toggle when cardPhase changes
  useEffect(() => {
    setManualToggle(null);
  }, [cardPhase]);

  // Determine expanded state:
  //  - During animation (activating/thinking/done): always expanded
  //  - When collapsed by parent: collapsed, unless manually toggled open
  const autoExpanded = cardPhase !== "collapsed";
  const showExpanded = manualToggle !== null ? manualToggle : autoExpanded;
  const NodeIcon = subCall.Icon;

  const mode = subCall.mode ?? "sequential";
  const childCount = subCall.subCalls?.length ?? 0;
  const isParallel = mode === "parallel";
  const isLoop = mode === "loop";

  // For sequential/loop: split duration evenly across children
  // For parallel: all children get the full duration
  const childDuration = isParallel
    ? durationMs
    : childCount > 0
      ? durationMs / childCount
      : 0;

  const [childPhases, setChildPhases] = useState<CardPhase[]>([]);

  useEffect(() => {
    if (!isThinking || childCount === 0) {
      setChildPhases([]);
      return;
    }

    const timers: ReturnType<typeof setTimeout>[] = [];

    if (isParallel) {
      // All children start at once
      setChildPhases(new Array(childCount).fill("activating"));
      timers.push(
        setTimeout(() => {
          setChildPhases(new Array(childCount).fill("thinking"));
        }, 100),
      );
      timers.push(
        setTimeout(() => {
          setChildPhases(new Array(childCount).fill("done"));
        }, durationMs * 0.85),
      );
    } else {
      // Sequential or loop — one at a time
      // Initialize: only first child visible, rest are null (hidden)
      const initial: (CardPhase | null)[] = new Array(childCount).fill(null);
      initial[0] = "activating";
      setChildPhases(initial as CardPhase[]);

      for (let i = 0; i < childCount; i++) {
        const startAt = i * childDuration;

        timers.push(
          setTimeout(() => {
            setChildPhases((prev) => {
              const next = [...prev];
              next[i] = "activating";
              return next;
            });
          }, startAt),
        );
        timers.push(
          setTimeout(() => {
            setChildPhases((prev) => {
              const next = [...prev];
              next[i] = "thinking";
              // For loop mode: collapse previous iteration
              if (isLoop && i > 0) {
                next[i - 1] = "collapsed";
              }
              return next;
            });
          }, startAt + 100),
        );
        timers.push(
          setTimeout(
            () => {
              setChildPhases((prev) => {
                const next = [...prev];
                next[i] = "done";
                return next;
              });
            },
            startAt + childDuration * 0.85,
          ),
        );
      }
    }

    return () => timers.forEach(clearTimeout);
  }, [isThinking, childCount, childDuration, isParallel, durationMs]);

  // Collapse children when parent collapses or finishes
  useEffect(() => {
    if (cardPhase === "collapsed" || cardPhase === "done") {
      if (childCount > 0) {
        setChildPhases((prev) => prev.map(() => "collapsed"));
      }
    }
  }, [cardPhase, childCount]);

  const thinkDur = durationMs * 0.55;
  const doneDur = durationMs * 0.45;

  // Mode label text
  const modeLabel = isLoop
    ? "loop"
    : isParallel
      ? "parallel"
      : childCount > 1
        ? "sequential"
        : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      style={{ width: "100%", margin: "4px 0" }}
    >
      <div
        className={cn(
          "rounded-[8px] [border-width:1px] [border-style:solid] border-border",
          depth === 0 ? "bg-muted" : "bg-card",
          "shadow-xs overflow-hidden",
        )}
      >
        {/* Header */}
        <div
          onClick={() =>
            isClickable && setManualToggle((p) => !(p ?? showExpanded))
          }
          className={cn(
            "flex items-center gap-1.5 pl-3 pr-3 pt-1.5 pb-1.5",
            isClickable ? "cursor-pointer" : "cursor-default",
            showExpanded
              ? "[border-bottom-width:1px] [border-bottom-style:solid]"
              : "[border-bottom-width:0] [border-bottom-style:solid]",
            "border-border",
            isClickable && "hover:bg-accent",
            "[transition:background-color_0.15s]",
          )}
        >
          <div className="w-4.5 h-4.5 rounded-[4px] bg-secondary [border-width:1px] [border-style:solid] border-border flex items-center justify-center text-[10px] shrink-0 text-muted-foreground/80">
            <NodeIcon size="1em" />
          </div>
          <Typography level="body-sm" className="[font-weight:600] flex-1">
            {iterationLabel ? `${iterationLabel} · ` : ""}
            {subCall.toolCallName}
          </Typography>

          {/* Status: spinner → stats + chevron */}
          {isDone ? (
            <>
              <StatsDisplay stats={subCall.stats} />
              <motion.div
                animate={{ rotate: showExpanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  display: "flex",
                  fontSize: "10px",
                  color: "inherit",
                  opacity: 0.4,
                }}
              >
                <icons.chevronDown size="1em" />
              </motion.div>
            </>
          ) : (
            <Spinner />
          )}
        </div>

        {/* Expandable body */}
        <motion.div
          initial={false}
          animate={{
            height: showExpanded ? "auto" : 0,
            opacity: showExpanded ? 1 : 0,
          }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
          style={{ overflow: "hidden" }}
        >
          <div className="pl-3 pr-3 pt-1.5 pb-1.5 flex flex-col gap-1">
            {/* Mode label inside body */}
            {modeLabel && childCount > 0 && (isThinking || isDone) && (
              <Typography
                level="body-xs"
                className={cn(
                  "font-mono text-[9px] [font-weight:600]",
                  isLoop
                    ? "text-primary"
                    : isParallel
                      ? "text-foreground"
                      : "text-muted-foreground/80",
                  "uppercase [letter-spacing:0.05em] opacity-[0.7] pt-0.5",
                )}
              >
                {modeLabel === "loop"
                  ? `↻ ${childCount} iterations`
                  : modeLabel === "parallel"
                    ? `⫘ ${childCount} parallel`
                    : `${childCount} steps`}
              </Typography>
            )}

            {/* Children cards or leaf thinking text */}
            {childCount > 0 ? (
              <div
                // Loop mode: colored vertical left border
                // Sequential (non-loop): subtle left border
                className={cn(
                  "flex flex-col gap-0.5",
                  isLoop &&
                    "[border-left-width:2px] [border-left-style:solid] border-l-ring pl-3 ml-1",
                  !isLoop &&
                    !isParallel &&
                    childCount > 1 &&
                    "[border-left-width:1px] [border-left-style:solid] border-l-border pl-3 ml-1",
                )}
              >
                {subCall.subCalls!.map((child, idx) => {
                  const cp = childPhases[idx];
                  if (!cp && !isDone) return null;
                  return (
                    <div key={idx} className="">
                      <SubCallCard
                        subCall={child}
                        cardPhase={cp ?? (isDone ? "collapsed" : "activating")}
                        durationMs={childDuration}
                        depth={depth + 1}
                        iterationLabel={
                          isLoop ? `${idx + 1}/${childCount}` : undefined
                        }
                        onScrollParent={onScrollParent}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              subCall.thinking &&
              (isThinking || isDone) && (
                <Typography
                  level="body-sm"
                  className="font-mono [line-height:1.4] italic text-muted-foreground/80 opacity-[0.8]"
                >
                  <StreamingMono
                    text={subCall.thinking}
                    isStreaming={isThinking}
                    durationMs={thinkDur}
                    onUpdate={onScrollParent}
                  />
                </Typography>
              )
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

// ── Top-level InlineCard (wraps SubCallCard for FlowNodeDef) ─────────────────

function InlineCard({
  node,
  toolCallName,
  cardPhase,
  thinkDurationMs,
  doneDurationMs,
  onScrollParent,
}: {
  node: FlowNodeDef;
  toolCallName: string;
  cardPhase: CardPhase;
  thinkDurationMs: number;
  doneDurationMs: number;
  onScrollParent: () => void;
}) {
  // Convert FlowNodeDef to a SubCallDef shape for the recursive component
  const asSubCall: SubCallDef = useMemo(
    () => ({
      toolCallName,
      Icon: node.Icon,
      input: node.input,
      thinking: node.thinking,
      output: node.output,
      stats: node.stats,
      subCalls: node.subCalls,
    }),
    [node, toolCallName],
  );

  return (
    <SubCallCard
      subCall={asSubCall}
      cardPhase={cardPhase}
      durationMs={thinkDurationMs + doneDurationMs}
      depth={0}
      onScrollParent={onScrollParent}
    />
  );
}

// ── Thread entry types ─────────────────────────────────────────────────────────

type ThreadEntry =
  | { type: "text"; text: string; isStreaming: boolean; durationMs: number }
  | {
      type: "card";
      node: FlowNodeDef;
      toolCallName: string;
      cardPhase: CardPhase;
      thinkDurationMs: number;
      doneDurationMs: number;
    };

// ── Streaming Thread Text ──────────────────────────────────────────────────────

function StreamingThreadText({
  text,
  isStreaming,
  durationMs,
  color,
  onUpdate,
}: {
  text: string;
  isStreaming: boolean;
  durationMs: number;
  color: string;
  onUpdate?: () => void;
}) {
  const safeText = text ?? "";
  const displayed = useStreamingText(safeText, isStreaming, durationMs);
  const showText = isStreaming ? displayed : safeText;
  const showCursor = isStreaming && displayed.length < safeText.length;

  useEffect(() => {
    if (isStreaming && onUpdate) onUpdate();
  }, [displayed, isStreaming, onUpdate]);

  if (!safeText) return null;

  return (
    <Typography
      level="body-xs"
      component="div"
      className="text-[13px] [line-height:1.65] text-muted-foreground pt-1 pb-1"
    >
      {showText}
      {showCursor && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{
            duration: 0.5,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          style={{ color }}
        >
          ▎
        </motion.span>
      )}
    </Typography>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

export function AgentOrchestration({ scenario }: { scenario: AgentScenario }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [phase, setPhase] = useState<Phase>("streaming-pre");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const step = scenario.steps[currentStep];

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  const phaseDuration = useMemo(() => {
    if (!step) return 0;
    switch (phase) {
      case "streaming-pre":
        return step.preDurationMs;
      case "card-thinking":
        return step.animDurationMs * 0.55;
      case "card-done":
        return step.animDurationMs * 0.45;
      case "streaming-post":
        return step.postDurationMs;
      case "pause":
        return step.pauseMs;
    }
  }, [step, phase]);

  useEffect(() => {
    if (!step) return;

    timerRef.current = setTimeout(() => {
      switch (phase) {
        case "streaming-pre":
          setPhase("card-thinking");
          break;
        case "card-thinking":
          setPhase("card-done");
          break;
        case "card-done":
          setPhase("streaming-post");
          break;
        case "streaming-post":
          setPhase("pause");
          break;
        case "pause": {
          const nextStep = currentStep + 1;
          if (nextStep < scenario.steps.length) {
            setCurrentStep(nextStep);
            setPhase("streaming-pre");
          }
          // else: animation finished — stay here, don't restart
          break;
        }
      }
    }, phaseDuration);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentStep, phase, step, scenario.steps.length, phaseDuration]);

  const currentColor = step
    ? scenario.getNodeColor(step.activeNodeId)
    : "var(--color-neutral-400)";

  const getNode = useCallback(
    (nodeId: string) => scenario.nodes.find((n) => n.id === nodeId)!,
    [scenario.nodes],
  );

  const threadEntries: ThreadEntry[] = useMemo(() => {
    const entries: ThreadEntry[] = [];

    for (let s = 0; s < currentStep; s++) {
      const prev = scenario.steps[s];
      const node = getNode(prev.activeNodeId);
      entries.push({
        type: "text",
        text: prev.preText,
        isStreaming: false,
        durationMs: 0,
      });
      entries.push({
        type: "card",
        node,
        toolCallName: prev.toolCallName,
        cardPhase: "collapsed",
        thinkDurationMs: 0,
        doneDurationMs: 0,
      });
      entries.push({
        type: "text",
        text: prev.postText,
        isStreaming: false,
        durationMs: 0,
      });
    }

    if (step) {
      const node = getNode(step.activeNodeId);

      entries.push({
        type: "text",
        text: step.preText,
        isStreaming: phase === "streaming-pre",
        durationMs: step.preDurationMs,
      });

      if (phase !== "streaming-pre") {
        let cp: CardPhase = "activating";
        if (phase === "card-thinking") cp = "thinking";
        else if (phase === "card-done") cp = "done";
        else cp = "collapsed";

        entries.push({
          type: "card",
          node,
          toolCallName: step.toolCallName,
          cardPhase: cp,
          thinkDurationMs: step.animDurationMs * 0.55,
          doneDurationMs: step.animDurationMs * 0.45,
        });
      }

      if (phase === "streaming-post" || phase === "pause") {
        entries.push({
          type: "text",
          text: step.postText,
          isStreaming: phase === "streaming-post",
          durationMs: step.postDurationMs,
        });
      }
    }

    return entries;
  }, [currentStep, phase, step, scenario, getNode]);

  useLayoutEffect(() => {
    scrollToBottom();
  }, [threadEntries, scrollToBottom]);

  return (
    <div className="w-full flex flex-col items-center">
      <Typography level="h2" className="text-center mb-2">
        {scenario.title}
      </Typography>
      <Typography
        level="body-md"
        className="text-center text-muted-foreground max-w-135 mb-10"
      >
        {scenario.subtitle}
      </Typography>

      <div className="w-full max-w-160 ml-auto mr-auto h-140 [border-width:1px] [border-style:solid] border-border rounded-lg bg-card overflow-hidden flex flex-col">
        <div className="pl-4 pr-4 pt-2.5 pb-2.5 [border-bottom-width:1px] [border-bottom-style:solid] border-border flex items-center gap-2 shrink-0">
          <div className="w-2 h-2 rounded-[50%] bg-muted-foreground" />
          <Typography
            level="body-xs"
            className="font-mono [font-weight:700] uppercase [letter-spacing:0.08em] text-muted-foreground/80 text-[10px]"
          >
            Agent Activity
          </Typography>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-auto pl-6 pr-6 pt-3 pb-3 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:rounded-[4px]"
        >
          <AnimatePresence initial={false}>
            {threadEntries.map((entry, i) => {
              if (entry.type === "text") {
                return (
                  <StreamingThreadText
                    key={`t-${i}`}
                    text={entry.text}
                    isStreaming={entry.isStreaming}
                    durationMs={entry.durationMs}
                    color={currentColor}
                    onUpdate={scrollToBottom}
                  />
                );
              }
              if (entry.type === "card") {
                return (
                  <InlineCard
                    key={`c-${i}`}
                    node={entry.node}
                    toolCallName={entry.toolCallName}
                    cardPhase={entry.cardPhase}
                    thinkDurationMs={entry.thinkDurationMs}
                    doneDurationMs={entry.doneDurationMs}
                    onScrollParent={scrollToBottom}
                  />
                );
              }
              return null;
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
